import type React from "react";
import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * Dense data table: sticky header, hairline rows, hover state, tabular figures.
 * Always wrapped in its own horizontal scroller so a wide table never makes the
 * page body scroll sideways.
 */
export function TableWrap({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="sticky top-0 z-10 bg-surface-sunken">
      <tr>{children}</tr>
    </thead>
  );
}

export function TH({
  children,
  numeric,
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        "whitespace-nowrap border-b border-line px-3 py-2 text-left text-xs font-medium text-muted",
        numeric && "text-right",
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-line">{children}</tbody>;
}

/**
 * `onClick` makes the whole row a mouse target. It is deliberately a
 * CONVENIENCE only: a `tr` cannot be given button semantics without breaking the
 * table for a screen reader, so any row that opens something must also carry a
 * real focusable control in one of its cells. Rows that do this pair the handler
 * here with a button in the cell the row is "about".
 */
export function TR({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
}) {
  return (
    <tr
      className={cn(
        "transition-colors hover:bg-surface-sunken/60",
        onClick && "cursor-pointer",
        className,
      )}
      {...(onClick ? { onClick } : {})}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  numeric,
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn(
        "px-3 py-2.5 align-middle text-ink-soft",
        numeric && "text-right tabular-nums",
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

/** The primary cell of a row: the thing the row is about. */
export function TDPrimary({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2.5 align-middle font-medium text-ink", className)}>{children}</td>;
}
